import { DEVELOPMENT_STAGES, getStage, type DevelopmentStage } from '@saesak/shared';
import { cn } from '@/lib/format';

/**
 * 발달 시각화 — 명세서 §6.4 / §6.8 / §7.4
 *
 * 색은 그린 계열 명도 스케일만 쓴다. 빨강·주황·노랑은 "판정"의 인상을 주므로 전면 금지(§7.4).
 * 학부모 화면에는 또래 비교를 절대 넣지 않는다(§6.1-3).
 */

const DARK = DEVELOPMENT_STAGES[6].color;

/**
 * 7단계 자체 컬러 일러스트 — 씨앗에서 열매까지 (§6.4, §7.3).
 *
 * 단계끼리 한눈에 구분되어야 하므로 형태를 단계마다 확실히 다르게 그린다.
 * (명도만 다르면 교사가 체크할 때 헷갈린다.)
 */
export function StageIcon({
  level,
  size = 28,
  foliage,
}: {
  level: number;
  size?: number;
  /** 짙은 배경 위에 올릴 때 잎 색을 덮어쓴다 */
  foliage?: string;
}) {
  const base = getStage(level);
  // L1·L2의 색은 배경으로 쓰기엔 좋지만 그림 자체에 쓰면 너무 옅어 형태가 안 보인다.
  // 잎 색은 최소 4단계(줄기) 명도 이상으로 잡아 어느 단계든 형태가 읽히게 한다.
  const stage = {
    ...base,
    color: foliage ?? DEVELOPMENT_STAGES[Math.max(level - 1, 3)].color,
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      role="img"
      aria-label={stage.name}
    >
      {/* 흙 — 모든 단계 공통 */}
      <path d="M5 27h22" stroke={DARK} strokeOpacity="0.3" strokeWidth="2" strokeLinecap="round" />

      {level === 1 && (
        // 씨앗 — 흙 아래에 잠들어 있다
        <>
          <ellipse cx="16" cy="22" rx="4.6" ry="3.6" fill={stage.color} stroke={DARK} strokeOpacity="0.4" />
          <path d="M13.6 21.2c1-1.2 2.6-1.9 4.4-1.9" stroke={DARK} strokeOpacity="0.3" strokeWidth="1.2" strokeLinecap="round" />
        </>
      )}

      {level === 2 && (
        // 새싹 — 흙을 뚫고 여린 싹 하나
        <>
          <path d="M16 27v-7" stroke={DARK} strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" />
          <path d="M16 20.5c0-3.4 1.8-5.5 5-5.8-.2 3.6-2 5.6-5 5.8Z" fill={stage.color} />
        </>
      )}

      {level === 3 && (
        // 떡잎 — 좌우 두 장이 마주 난다
        <>
          <path d="M16 27v-9" stroke={DARK} strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" />
          <path d="M16 19.5c-3.2 0-5-2-5.2-5.6 3.4.2 5.2 2.2 5.2 5.6Z" fill={stage.color} />
          <path d="M16 19.5c3.2 0 5-2 5.2-5.6-3.4.2-5.2 2.2-5.2 5.6Z" fill={stage.color} fillOpacity="0.7" />
        </>
      )}

      {level === 4 && (
        // 줄기 — 키가 자라고 잎이 두 쌍
        <>
          <path d="M16 27V10" stroke={DARK} strokeOpacity="0.55" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M16 20c-3.4 0-5.4-2.1-5.6-6 3.6.2 5.6 2.3 5.6 6Z" fill={stage.color} />
          <path d="M16 15c3.4 0 5.4-2.1 5.6-6-3.6.2-5.6 2.3-5.6 6Z" fill={stage.color} fillOpacity="0.75" />
        </>
      )}

      {level === 5 && (
        // 꽃봉오리 — 줄기 끝에 아직 다물린 봉오리
        <>
          <path d="M16 27V13" stroke={DARK} strokeOpacity="0.55" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M16 22c-3.2 0-5.2-2-5.4-5.6 3.4.2 5.4 2.2 5.4 5.6Z" fill={stage.color} fillOpacity="0.85" />
          <path
            d="M16 5c2.6 1.6 4 3.7 4 6.2 0 2.4-1.8 3.9-4 3.9s-4-1.5-4-3.9C12 8.7 13.4 6.6 16 5Z"
            fill={stage.color}
            stroke={DARK}
            strokeOpacity="0.25"
          />
        </>
      )}

      {level >= 6 && (
        // 꽃 — 다섯 장 꽃잎이 활짝
        <>
          <path d="M16 27V14" stroke={DARK} strokeOpacity="0.55" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M16 23c-3 0-4.9-1.9-5.1-5.3 3.2.2 5.1 2.1 5.1 5.3Z" fill={stage.color} fillOpacity="0.8" />
          {[0, 72, 144, 216, 288].map((angle) => (
            <ellipse
              key={angle}
              cx="16"
              cy="5.8"
              rx="2.7"
              ry="4.2"
              fill={stage.color}
              transform={`rotate(${angle} 16 10)`}
            />
          ))}
          <circle cx="16" cy="10" r="2.5" fill={level === 7 ? '#FFD66E' : DARK} fillOpacity={level === 7 ? 1 : 0.45} />
        </>
      )}

      {level === 7 && (
        // 열매 — 곁가지에 열매가 맺힌다
        <>
          <path d="M16 19c-2.6.4-4.3 1.4-5.2 3" stroke={DARK} strokeOpacity="0.4" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M16 19c2.6.4 4.3 1.4 5.2 3" stroke={DARK} strokeOpacity="0.4" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="9.4" cy="23.4" r="3" fill="#FFB49A" />
          <circle cx="22.6" cy="23.4" r="3" fill="#FFB49A" />
        </>
      )}
    </svg>
  );
}

export function StageBadge({
  level,
  showName = true,
  size = 'md',
}: {
  level: number;
  showName?: boolean;
  size?: 'sm' | 'md';
}) {
  const stage = getStage(level);
  // 연한 배경에는 짙은 글자, 짙은 배경에는 흰 글자.
  const light = level <= 4;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold',
        size === 'sm' ? 'px-2 py-0.5 text-[12px]' : 'px-3 py-1 text-sm',
      )}
      style={{ backgroundColor: stage.color, color: light ? '#2E5F4C' : '#FFFFFF' }}
    >
      {/* 짙은 배경(L5~L7)에서는 잎을 흰색으로 바꿔야 형태가 보인다 */}
      <StageIcon
        level={level}
        size={size === 'sm' ? 16 : 20}
        foliage={light ? undefined : '#FFFFFF'}
      />
      {showName && stage.name}
    </span>
  );
}

/** 단계 스케일 범례 — 7단계가 우열이 아니라 여정임을 보여 준다 */
export function StageScale({ current }: { current?: number | null }) {
  return (
    <div className="scroll-x -mx-1">
      <ol className="flex w-max gap-1.5 px-1">
        {DEVELOPMENT_STAGES.map((stage) => (
          <li
            key={stage.code}
            className={cn(
              'flex w-[62px] flex-col items-center gap-1 rounded-sm px-1 py-2 text-center',
              current === stage.level && 'ring-2 ring-primary-strong',
            )}
            style={{ backgroundColor: `${stage.color}66` }}
          >
            <StageIcon level={stage.level} size={22} />
            <span className="text-[11px] font-semibold text-ink">{stage.name}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export interface TreeBranch {
  domainCode: string;
  domainName: string;
  icon: string;
  level: number | null;
  stage: DevelopmentStage | null;
  observedItemCount: number;
}

/**
 * 새싹 나무 — 서비스명("새싹돋보기")과 이어지는 시그니처 화면 (§6.8).
 * 영역 6개를 좌우 가지로 표현하고, 각 가지 끝에 현재 단계 아이콘을 얹는다.
 *
 * 줄기·가지는 고정 좌표 SVG가 아니라 레이아웃 요소로 그린다.
 * (텍스트 길이나 글자 크기에 따라 행 높이가 달라져도 선이 어긋나지 않게 하기 위해서다.)
 */
export function SeedlingTree({
  branches,
  onSelect,
  selected,
}: {
  branches: TreeBranch[];
  onSelect?: (domainCode: string) => void;
  selected?: string | null;
}) {
  // 좌우 한 쌍씩 3줄로 배치한다.
  const rows: [TreeBranch | undefined, TreeBranch | undefined][] = [
    [branches[0], branches[3]],
    [branches[1], branches[4]],
    [branches[2], branches[5]],
  ];

  return (
    <div className="relative overflow-hidden rounded-md bg-primary-soft/60 px-2 py-5">
      {/* 줄기 */}
      <span
        aria-hidden
        className="absolute bottom-3 left-1/2 top-6 w-1.5 -translate-x-1/2 rounded-full"
        style={{ backgroundColor: 'var(--stage-4)' }}
      />

      <div className="relative flex flex-col gap-3">
        {rows.map(([left, right], index) => (
          <div key={index} className="flex items-center">
            <div className="flex min-w-0 flex-1 justify-end">
              {left && (
                <BranchLeaf
                  branch={left}
                  align="left"
                  onSelect={onSelect}
                  selected={selected === left.domainCode}
                />
              )}
            </div>

            {/* 가지 — 좌우 잎을 잇는 가로선이 줄기를 지난다 */}
            <span
              aria-hidden
              className="h-1.5 w-5 shrink-0 rounded-full"
              style={{ backgroundColor: 'var(--stage-4)' }}
            />

            <div className="flex min-w-0 flex-1 justify-start">
              {right && (
                <BranchLeaf
                  branch={right}
                  align="right"
                  onSelect={onSelect}
                  selected={selected === right.domainCode}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BranchLeaf({
  branch,
  align,
  onSelect,
  selected,
}: {
  branch: TreeBranch;
  align: 'left' | 'right';
  onSelect?: (domainCode: string) => void;
  selected?: boolean;
}) {
  const content = (
    <>
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 bg-surface"
        style={{ borderColor: branch.stage?.color ?? 'var(--color-border-strong)' }}
      >
        {branch.level ? (
          <StageIcon level={branch.level} size={22} />
        ) : (
          <span className="text-[11px] text-ink-sub">–</span>
        )}
      </span>
      {/* 영역명은 줄여 쓰지 않고 두 줄로 흘린다 — 좁은 화면에서 "신체 — 대..."로 잘리면
          어느 영역인지 알 수 없기 때문이다. */}
      <span className={cn('min-w-0 leading-tight', align === 'right' && 'text-right')}>
        <span className="block text-[12px] font-semibold text-ink">{branch.domainName}</span>
        <span className="block text-[11px] text-ink-sub">
          {branch.stage ? branch.stage.name : '기록 준비 중'}
        </span>
      </span>
    </>
  );

  const className = cn(
    'flex w-full items-center gap-1.5 rounded-md bg-surface/90 px-2 py-2 text-left transition-all',
    align === 'right' && 'flex-row-reverse',
    selected && 'ring-2 ring-primary-strong',
  );

  if (!onSelect) return <div className={className}>{content}</div>;

  return (
    <button
      type="button"
      className={cn(className, 'active:scale-[0.98]')}
      onClick={() => onSelect(branch.domainCode)}
    >
      {content}
    </button>
  );
}
